import { Stack } from '@mui/material';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router';
import type { DialogType } from '../../../@types';
import type { AffiliateCampaign } from '../../../@types/api';
import { QUERY_KEYS } from '../../../constants';
import API_ROUTES from '../../../constants/apiRoutes';
import { useModal } from '../../../contexts/ModalContext';
import useCampaignOptionHydration from '../../../hooks/useCampaignOptionHydration';
import useLocationData from '../../../hooks/useLocationData';
import type { AdditionalQueryParams } from '../../../hooks/usePagination';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormNumberInput from '../../molecules/FormNumberInput';
import FormAutocomplete from '../FormAutocomplete';
import { CreatedAtFilter, StatusFilter, StringArrayFilter } from './CRMFilterFields';
import { playerFilterParams, type PlayerFilterData } from './playerFilterParams';

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

export const getCampaignOptionLabel = (options: AffiliateCampaign[], value: string) =>
  options.find((option) => option.id === value)?.name ?? '';

const FilterPlayers = ({ changeQuery }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');

  const { closeModal } = useModal();
  const { control, handleSubmit, setValue, watch } = useForm<PlayerFilterData>({
    defaultValues: playerFilterParams.parse(searchParams),
  });

  const clearUnavailableCampaign = useCallback(() => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.delete('campaignId');
    setValue('campaignId', null);
    setSearchParams(updatedSearchParams);
    changeQuery({});
  }, [changeQuery, searchParams, setSearchParams, setValue]);

  const initialCampaignOptions = useCampaignOptionHydration(campaignId, clearUnavailableCampaign);

  const selectedCountries = watch('country');
  const { getLocationOptions } = useLocationData(selectedCountries);

  const applyFilters = (data: PlayerFilterData) => {
    setSearchParams(playerFilterParams.write(searchParams, data));
    changeQuery({});
    closeModal();
  };

  const resetFilters = () => {
    setSearchParams(playerFilterParams.write(searchParams, playerFilterParams.parse(new URLSearchParams())));
    changeQuery({});
    closeModal();
  };

  return (
    <FilterDialogLayout label='players' onClose={closeModal} onSave={handleSubmit(applyFilters)} onReset={resetFilters}>
      <FormAutocomplete<AffiliateCampaign, PlayerFilterData>
        control={control}
        name='campaignId'
        label='Campaign'
        url={API_ROUTES.CAMPAIGNS}
        apiService='crm'
        queryKey={[QUERY_KEYS.campaigns, 'player-filter']}
        initialOptions={initialCampaignOptions}
        getOptionLabel={getCampaignOptionLabel}
      />
      <StatusFilter control={control} name='isActive' />
      <StringArrayFilter control={control} name='country' label='Country' options={getLocationOptions('country')} />
      <CreatedAtFilter control={control} fromName='createdAt.from' toName='createdAt.to' />
      <StringArrayFilter control={control} name='firstName' label='First Name' freeSolo />
      <StringArrayFilter control={control} name='lastName' label='Last Name' freeSolo />
      <StringArrayFilter control={control} name='phone' label='Phone Number' freeSolo />
      <StringArrayFilter control={control} name='email' label='Email' freeSolo />
      <StringArrayFilter control={control} name='zip' label='Zip Code' freeSolo />
      <StringArrayFilter control={control} name='city' label='City' options={getLocationOptions('city')} />
      <StringArrayFilter control={control} name='region' label='Region' options={getLocationOptions('region')} />
      <StringArrayFilter control={control} name='language' label='Language' freeSolo />
      <Stack direction='row' spacing={2}>
        <FormNumberInput control={control} name='age.from' label='Age from' fullWidth valueAsString />
        <FormNumberInput control={control} name='age.to' label='Age to' fullWidth valueAsString />
      </Stack>
    </FilterDialogLayout>
  );
};

export default FilterPlayers;
